import { Text, Timeline, useMatches } from "@mantine/core";
import { IconBriefcaseFilled, IconTrendingUp, IconStar } from "@tabler/icons-react";
import { useState } from "react";
import { ExperienceInfo } from "../User";

const groupedExperiences = ExperienceInfo.reduce((acc: { [key: string]: any[] }, exp) => {
    const { company } = exp;
    if (!acc[company]) {
        acc[company] = [];
    }
    acc[company].push(exp);
    return acc;
}, {});

const TimelineItem = ({ items }: { items: { [key: string]: any[] } }) => {
    const size = useMatches({
        xs: 15,
        md: 25,
    });

    return (
        <>
            {Object.keys(items).map((company, idx) => (
                <Timeline.Item
                    key={idx}
                    bullet={<IconBriefcaseFilled className="!text-bgColor !bg-primaryColor rounded-full p-1" size={size} />}
                    className="!pt-6"
                    data-aos="fade-left"
                    data-aos-duration="800"
                >
                    {items[company].length > 1 ? (
                        <Timeline
                            color="#64FFDA"
                            active={items[company].length}
                            bulletSize={size}
                            lineWidth={1}
                            data-aos="fade-right"
                            data-aos-duration="800"
                            styles={{
                                root: {
                                    '& .mantine-Timeline-itemBullet': {
                                        backgroundColor: '#64FFDA !important',
                                        border: '2px solid #64FFDA !important'
                                    }
                                }
                            }}
                        >
                            {items[company]
                                .sort((a, b) => new Date(b.date.split(' - ')[0]).getTime() - new Date(a.date.split(' - ')[0]).getTime())
                                .map((item: any, index: number) => (
                                <Timeline.Item
                                    key={index}
                                    bullet={
                                        item.promotion ? (
                                            <div className="relative">
                                                <IconTrendingUp className="!text-bgColor" size={size} />
                                                <IconStar className="absolute -top-1 -right-1 text-yellow-400" size={12} />
                                            </div>
                                        ) : (
                                            <IconBriefcaseFilled className="!text-bgColor" size={size} />
                                        )
                                    }
                                    className="!pt-6"
                                    lineVariant="dotted"
                                    data-aos="fade-left"
                                    data-aos-duration="800"
                                >
                                    <div className={`border shadow-[0_0_10px_0_#64FFDA50] hover:-translate-y-2 transition transform duration-300 ease-in-out flex flex-col gap-2 p-4 rounded-2xl sm-mx:p-2 ${
                                        item.promotion 
                                            ? 'border-yellow-400 shadow-[0_0_15px_0_#FFD70050] bg-gradient-to-br from-yellow-900/10 to-transparent' 
                                            : 'border-primaryColor'
                                    }`}>
                                        {item.promotion && (
                                            <div className="flex items-center gap-2 mb-2">
                                                <IconTrendingUp size={16} className="text-yellow-400" />
                                                <span className="text-yellow-400 text-sm font-semibold">Promotion</span>
                                            </div>
                                        )}
                                        <div className="flex gap-2 items-center">
                                            <div className="w-16 h-16 md-mx:w-14 md-mx:h-14 rounded-lg bg-gradient-to-br from-primaryColor/20 to-primaryColor/5 flex items-center justify-center border border-primaryColor/30">
                                                <IconBriefcaseFilled className="text-primaryColor" size={24} />
                                            </div>
                                            <div className="flex flex-col">
                                                <div
                                                    className="text-white text-2xl font-semibold sm-mx:text-xl xs-mx:text-lg xsm-mx:text-base"
                                                    data-aos="fade-left"
                                                    data-aos-duration="800"
                                                >
                                                    {item.role}
                                                </div>
                                                <div
                                                    className="text-lg font-semibold text-textColor md-mx:text-base sm-mx:text-sm xs-mx:text-xs"
                                                    data-aos="fade-left"
                                                    data-aos-duration="800"
                                                >
                                                    {item.company} &#x2022; {item.date}
                                                </div>
                                                {item.date.includes('Present') && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                                        <span className="text-green-400 text-xs font-medium">Currently Active</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className="text-textColor leading-6 text-justify md-mx:text-sm xs-mx:text-xs"
                                            data-aos="fade-left"
                                            data-aos-duration="800"
                                        >
                                            {item.desc}
                                        </div>
                                        <div className="text-lg font-medium text-textColor md-mx:text-base sm-mx:text-sm xs-mx:text-xs flex gap-1">
                                            <div
                                                className="font-semibold text-white"
                                                data-aos="fade-left"
                                                data-aos-duration="800"
                                            >
                                                Skills:
                                            </div>
                                            <div className="flex gap-1 flex-wrap">
                                                {item.skills.map((skill: any, skillIndex: number) => (
                                                    <span
                                                        key={skillIndex}
                                                        className="px-2 py-1 bg-primaryColor/10 text-primaryColor rounded-md text-xs border border-primaryColor/20 hover:bg-primaryColor/20 transition-colors duration-200"
                                                        data-aos="fade-left"
                                                        data-aos-duration="800"
                                                    >
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    ) : (
                        items[company].map((item: any, index: number) => (
                            <div
                                key={index}
                                className={`border shadow-[0_0_10px_0_#64FFDA50] hover:-translate-y-2 transition transform duration-300 ease-in-out flex flex-col gap-2 p-4 rounded-2xl sm-mx:p-2 ${
                                    item.promotion 
                                        ? 'border-yellow-400 shadow-[0_0_15px_0_#FFD70050] bg-gradient-to-br from-yellow-900/10 to-transparent' 
                                        : 'border-primaryColor'
                                }`}
                                data-aos="fade-left"
                                data-aos-duration="800"
                            >
                                {item.promotion && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <IconTrendingUp size={16} className="text-yellow-400" />
                                        <span className="text-yellow-400 text-sm font-semibold">Promotion</span>
                                    </div>
                                )}
                                <div className="flex gap-2 items-center">
                                    <div className="w-16 h-16 md-mx:w-14 md-mx:h-14 rounded-lg bg-gradient-to-br from-primaryColor/20 to-primaryColor/5 flex items-center justify-center border border-primaryColor/30">
                                        <IconBriefcaseFilled className="text-primaryColor" size={24} />
                                    </div>
                                    <div className="flex flex-col">
                                        <div
                                            className="text-white text-2xl font-semibold sm-mx:text-xl xs-mx:text-lg xsm-mx:text-base"
                                            data-aos="fade-left"
                                            data-aos-duration="800"
                                        >
                                            {item.role}
                                        </div>
                                        <div
                                            className="text-lg font-semibold text-textColor md-mx:text-base sm-mx:text-sm xs-mx:text-xs"
                                            data-aos="fade-left"
                                            data-aos-duration="800"
                                        >
                                            {item.company} &#x2022; {item.date}
                                        </div>
                                        {item.date.includes('Present') && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                                <span className="text-green-400 text-xs font-medium">Currently Active</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="text-textColor leading-6 text-justify md-mx:text-sm xs-mx:text-xs"
                                    data-aos="fade-left"
                                    data-aos-duration="800"
                                >
                                    {item.desc}
                                </div>
                                <div className="text-lg font-medium text-textColor md-mx:text-base sm-mx:text-sm xs-mx:text-xs flex gap-1">
                                    <div
                                        className="font-semibold text-white"
                                        data-aos="fade-left"
                                        data-aos-duration="800"
                                    >
                                        Skills:
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                        {item.skills.map((skill: any, skillIndex: number) => (
                                            <span
                                                key={skillIndex}
                                                className="px-2 py-1 bg-primaryColor/10 text-primaryColor rounded-md text-xs border border-primaryColor/20 hover:bg-primaryColor/20 transition-colors duration-200"
                                                data-aos="fade-left"
                                                data-aos-duration="800"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </Timeline.Item>
            ))}
        </>
    );
};

const Experience = () => {
    const size = useMatches({
        xs: 15,
        md: 20,
    });
    const dot = useMatches({
        xs: 25,
        md: 30,
    });
    const [showAll, setShowAll] = useState(false);

    const visibleExperiences = showAll
        ? Object.keys(groupedExperiences)
        : Object.keys(groupedExperiences).slice(0, 3);

    const totalExperiences = ExperienceInfo.length;
    const visibleCount = showAll ? totalExperiences : Math.min(totalExperiences, 3);

    return (
        <div
            className="px-16 mx-20 md-mx:px-6 sm-mx:px-2 lg-mx:mx-0 my-10 mb-28 font-mono"
            id="Experience"
        >
            <div className="text-center mb-10">
                <h1 className="text-4xl sm-mx:text-3xl xs-mx:text-2xl mb-4 font-bold text-white">
                    <span className="text-primaryColor">04.&nbsp;</span>Experience
                </h1>
                <div className="flex items-center justify-center gap-4 text-textColor text-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-primaryColor rounded-full"></div>
                        <span>Regular Position</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                        <span>Promotion</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span>Currently Active</span>
                    </div>
                </div>
            </div>
            
            <Timeline
                color="#64FFDA"
                active={visibleCount}
                bulletSize={dot}
                lineWidth={2}
                data-aos="fade-right"
                data-aos-duration="800"
                styles={{
                    root: {
                        '& .mantine-Timeline-itemBullet': {
                            backgroundColor: '#64FFDA !important',
                            border: '2px solid #64FFDA !important'
                        }
                    }
                }}
            >
                {visibleExperiences.map((company) => (
                    <TimelineItem
                        key={company}
                        data-aos="fade-left"
                        data-aos-duration="800"
                        items={{ [company]: groupedExperiences[company] }}
                    />
                ))}
                <Timeline.Item
                    bullet={
                        <div className="relative">
                            <IconBriefcaseFilled
                                className="!text-bgColor bg-primaryColor rounded-full p-1"
                                size={size}
                            />
                            <div className="absolute inset-0 bg-primaryColor rounded-full animate-ping opacity-20"></div>
                        </div>
                    }
                    className="!pb-0"
                >
                    <div className="text-center text-textColor text-sm font-medium">
                        {showAll ? "Journey Continues..." : `Showing ${visibleCount} of ${totalExperiences} experiences`}
                    </div>
                </Timeline.Item>
            </Timeline>
            
            {!showAll && Object.keys(groupedExperiences).length > 3 && (
                <div className="flex justify-center mt-8">
                    <button
                        onClick={() => setShowAll(true)}
                        className="focus-visible:!outline-none text-primaryColor hover:text-white transition-all duration-300 flex items-center gap-2 px-4 py-2 border border-primaryColor/30 rounded-lg hover:border-primaryColor hover:shadow-[0_0_10px_0_#64FFDA30]"
                    >
                        <span>Show More Experiences</span>
                        <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default Experience;